/*
=============================================================
 *   AUTHENTICATION
 *   Auth provider to manage auth functions throughout
 *   the application. 
 *   PrivateRoute component to
 *   protect internal application routes from unauthenticated
 *   access.
 ============================================================
 */

import { useState, useEffect, createContext, useContext } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';

// auth context
export const AuthContext = createContext();

const useAPI = require('components/lib').useAPI;
const Event = require('components/lib').Event;

/* 
=====================================================
   AUTH PROVIDER
=====================================================
*/
export function AuthProvider(props) {
	const cache = JSON.parse(localStorage.getItem('user'));
	const [user, setUser] = useState(cache);

	const auth = useAPI(user ? '/api/auth' : null);

	useEffect(() => {
		// update the auth status
		if (!auth.loading && auth.data) {
			auth.data.authenticated ? update(auth.data) : signout();
		}
	}, [auth]);

	/* ---------------- SIGNIN ---------------- */

	function signin(res) {
		if (!res.data) return;

		localStorage.setItem('user', JSON.stringify(res.data));
		axios.defaults.headers.common['Authorization'] = 'Bearer ' + res.data.token;
		setUser(res.data);

		Event('signin');

		update({ stop_walkthrough: [] });

		if (!res.data.verified) {
			return (window.location = '/signup/verify');
		}

		if (!res.data.plan) {
			return (window.location = '/signup/plan');
		}

		return (window.location = res?.data?.redirectLink ? res.data?.redirectLink : '/navigate');
	}

	/* ---------------- SIGNOUT ---------------- */

	async function signout() {
		try {
			await axios.delete('/api/auth');
		} catch {}

		localStorage.removeItem('user');
		setUser(null);

		return (window.location = '/signin');
	}

	/* ---------------- SWITCH ACCOUNT ---------------- */

	async function switchAccount(id) {
		const res = await axios.post('/api/auth/switch', {
			account: id,
		});

		if (res.data) signin(res);
	}

	function update(data) {
		const stored = localStorage.getItem('user');
		if (!stored) return;

		let user = JSON.parse(stored);

		for (let key in data) {
			if (Array.isArray(data[key])) {
				user[key] = data[key];
			} else if (typeof data[key] === 'object') {
				for (let innerKey in data[key]) {
					user[key][innerKey] = data[key][innerKey];
				}
			} else {
				user[key] = data[key];
			}
		}

		localStorage.setItem('user', JSON.stringify(user));
		setUser(user);
	}

	return (
		<AuthContext.Provider
			value={{
				user: user,
				signin: signin,
				signout: signout,
				update: update,
				switchAccount: switchAccount,
				permission: user?.role_permissions || {},
			}}
			{...props}
		/>
	);
}

/*
 =====================================================
   PRIVATE ROUTE
======================================================
*/
export function PrivateRoute(props) {
	// check user exists
	const { user } = useContext(AuthContext);

	const path = window.location.pathname;

	const permittedRoutes = [
		'/account/billing',
		'/signup/plan',
		'/signup/mobile',
		'/account/upgrade',
		'/account',
		'/account/profile',
		'/actionrequired',
		'/navigate',
		'/account/password',
		'/account/2fa',
		'/help',
	];

	const allowedStatuses = [
		'active',
		'expired',
		'subscription_expired',
		'expiring_soon',
		'trialing',
		'external_subscription_expired',
	];

	/* ---------- NOT AUTHENTICATED ---------- */

	if (!user?.token) {
		localStorage.clear();
		return <Navigate to="/signin" />;
	}

	/* ---------- PERMISSION CHECK ---------- */
	if (!user?.role_permissions?.[props.permission]) {
		if (user?.role_permissions?.['navigate.view']) {
			return <Navigate to="/navigate" />;
		}
		return <Navigate to="/account" />;
	}

	/* ---------- PLAN RESTRICTIONS ---------- */

	if (user.plan === 'free' && path === '/labs/all') return <Navigate to="/labs/purchase" />;

	if (user.plan === 'free' && path === '/projects/all') return <Navigate to="/projects/purchase" />;

	if ((user.plan === 'free' || user.plan === 'hybrid') && path === '/streams/all')
		return <Navigate to="/streams/purchase" />;

	if ((user.plan === 'free' || user.plan === 'hybrid') && path === '/lpaths/all')
		return <Navigate to="/lpaths/purchase" />;

	if (!(user.plan === 'free' || user.plan === 'hybrid') && path === '/labs/purchase')
		return <Navigate to="/labs/all" />;

	if (!(user.plan === 'free' || user.plan === 'hybrid') && path === '/projects/purchase')
		return <Navigate to="/projects/all" />;

	if (!(user.plan === 'free' || user.plan === 'hybrid') && path === '/streams/purchase')
		return <Navigate to="/streams/all" />;

	/* ---------- VERIFICATION ---------- */

	if (!user.verified) {
		if (path !== '/account/profile' && path !== '/signup/verify') return <Navigate to="/signup/verify" />;

		return props.children;
	}

	if (path === '/signup/mobile') return <Navigate to="/navigate" />;

	if (!user.is_org_logo_upload_required && path === '/signup/logo') return <Navigate to="/navigate" />;

	if (user.is_org_logo_upload_required && path !== '/signup/logo') return <Navigate to="/signup/logo" />;

	if (path === '/signup/verify') return <Navigate to="/streams" />;

	/* ---------- ONBOARDING + PLAN ---------- */

	if (!user.onboarded && path.startsWith('/account/')) return <Navigate to="/account" />;

	if (!user.plan && user.onboarded && path !== '/account/profile' && path !== '/signup/plan')
		return <Navigate to="/signup/plan" />;

	if (user.plan && path === '/signup/plan') return <Navigate to="/account" />;

	/* ---------- SUBSCRIPTION ---------- */
	const subscriptionInvalid =
		!allowedStatuses.includes(user.subscription) &&
		!user?.role_permissions?.['mc.subscription.bypass'] &&
		!permittedRoutes.includes(path) &&
		!path.startsWith('/quiz');

		if (subscriptionInvalid) {
		if (['past_due', 'disabled', 'disabled_org'].includes(user.subscription)) {
			return user?.role_permissions['billing.view'] ? <Navigate to="/account/billing" /> : <Navigate to="/actionrequired" />;
		}

		return <Navigate to="/account/billing" />;
	}

	/* ---------- SUCCESS ---------- */

	return props.children;
}
